/* Sticky Johnson team page behaviour (sections/sj-team-*.liquid). Loaded with defer by each team section; runs once.
 * - Entrances: adds .sjt--anim to each .sjt root (not under reduced motion), then .is-in to items as they enter
 *   the viewport, staggered 50ms within each batch (capped). Content is visible without JS.
 * - Roster: region chips (aria-pressed), "x of y shown" count, Load more. Without JS every card shows.
 * - Hero video: plays muted inline, pause/play button; starts paused under reduced motion.
 * - Feed videos: play muted while on screen, pause off screen (native controls always on); no autoplay under reduced motion.
 */
(function () {
  if (window.SJTeam) return;
  window.SJTeam = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- entrances ---------- */
  var io = null;
  if (!reduced && hasIO) {
    io = new IntersectionObserver(function (entries) {
      var n = 0;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.style.setProperty('--sjt-d', Math.min(n, 8) * 50 + 'ms');
        e.target.classList.add('is-in');
        io.unobserve(e.target);
        n++;
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.05 });
  }

  function watch(el) {
    if (!io) { el.classList.add('is-in'); return; }
    el.classList.remove('is-in');
    io.observe(el);
  }

  function initReveal(root) {
    if (!io || root.dataset.sjtAnim) return;
    root.dataset.sjtAnim = '1';
    root.classList.add('sjt--anim');
    root.querySelectorAll('[data-sjt-reveal] > *, .sjr-card').forEach(function (el) {
      if (!el.hidden) watch(el);
    });
  }

  /* ---------- roster ---------- */
  function initRoster(root) {
    if (root.dataset.sjtRoster === 'ready') return;
    root.dataset.sjtRoster = 'ready';
    var cards = Array.prototype.slice.call(root.querySelectorAll('.sjr-card'));
    var chipsWrap = root.querySelector('[data-sjt-chips]');
    var chips = chipsWrap ? Array.prototype.slice.call(chipsWrap.querySelectorAll('.sjt-chip')) : [];
    var count = root.querySelector('[data-sjt-count]');
    var more = root.querySelector('[data-sjt-more]');
    var step = parseInt(root.dataset.step, 10) || 12;
    var tpl = root.dataset.countTemplate || '[shown] of [total] shown';
    var filter = 'all';
    var limit = step;

    function matching() {
      return cards.filter(function (c) { return filter === 'all' || c.dataset.country === filter; });
    }

    function render(revealFrom) {
      var list = matching();
      var fresh = [];
      cards.forEach(function (c) { c.hidden = true; });
      list.forEach(function (c, i) {
        if (i < limit) {
          c.hidden = false;
          if (revealFrom !== undefined && i >= revealFrom) fresh.push(c);
        }
      });
      var shown = Math.min(limit, list.length);
      if (count) {
        count.textContent = tpl.replace('[shown]', shown).replace('[total]', list.length);
        count.hidden = false;
      }
      if (more) more.hidden = shown >= list.length;
      if (root.classList.contains('sjt--anim')) fresh.forEach(watch);
      return fresh;
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.getAttribute('aria-pressed') === 'true') return;
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        filter = chip.dataset.value;
        limit = step;
        render(0);
      });
    });

    if (more) {
      more.addEventListener('click', function () {
        var from = limit;
        limit += step;
        var fresh = render(from);
        /* keep keyboard users in place: focus the first new rider's name link */
        var link = fresh[0] && fresh[0].querySelector('.sjr-card__name a');
        if (link) link.focus({ preventScroll: false });
      });
    }

    if (chipsWrap) chipsWrap.hidden = false;
    render();
  }

  /* ---------- hero video ---------- */
  function initHeroVideo(root) {
    var video = root.querySelector('[data-sjt-autoplay]');
    var btn = root.querySelector('[data-sjt-video-toggle]');
    if (!video || video.dataset.sjtReady) return;
    video.dataset.sjtReady = '1';
    var label = btn && btn.querySelector('[data-sjt-pause-label]');
    var pauseText = label ? label.textContent : 'Pause video';
    var playText = pauseText.replace(/pause/i, function (m) { return m[0] === 'P' ? 'Play' : 'play'; });

    function sync() {
      if (!btn) return;
      var paused = video.paused;
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      if (label) label.textContent = paused ? playText : pauseText;
    }
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    if (btn) {
      btn.hidden = false;
      btn.addEventListener('click', function () {
        if (video.paused) { video.play().catch(function () {}); } else { video.pause(); }
      });
    }
    if (!reduced) {
      video.muted = true;
      var p = video.play();
      if (p && p.catch) p.catch(function () { sync(); });
    }
    sync();
  }

  /* ---------- feed videos ---------- */
  function initFeedVideos(root) {
    var vids = root.querySelectorAll('[data-sjt-inview-play]');
    if (!vids.length || reduced || !hasIO) return;
    var vio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { v.pause(); }
      });
    }, { threshold: 0.5 });
    vids.forEach(function (v) {
      if (v.dataset.sjtReady) return;
      v.dataset.sjtReady = '1';
      v.muted = true; /* native controls stay on so anyone can pause */
      v.preload = 'metadata';
      vio.observe(v);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('.sjt').forEach(function (root) {
      if (root.hasAttribute('data-sjt-roster')) initRoster(root);
      if (root.classList.contains('sjt-hero')) initHeroVideo(root);
      if (root.classList.contains('sjt-feed')) initFeedVideos(root);
      initReveal(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* theme editor: re-run for a section when it's added or re-rendered */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
